import { useQuery } from '@apollo/client';
import { DetailBookingConfirmed } from 'components/detail/view/DetailBooking/DetailBookingConfirmed';
import { DetailBookingFailed } from 'components/detail/view/DetailBooking/DetailBookingFailed';
import { DetailBookingForm } from 'components/detail/view/DetailBooking/DetailBookingForm';
import { DetailBookingFull } from 'components/detail/view/DetailBooking/DetailBookingFull';
import {
    BookingQueryView,
    BookingQueryViewVariables
} from 'graphql/query/__generated__/BookingQueryView';
import { BOOKING_QUERY_VIEW } from 'graphql/query/detailQuery';
import { useContext, useEffect, useState } from 'react';
import { CtxEdit } from 'session/ctxEdit';
import { CtxView } from 'session/ctxView';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    markerId: string;
}

export const DetailBooking = (props: Props): JSX.Element => {
    const ctxView = useContext(CtxView);
    const ctxEdit = useContext(CtxEdit);
    const worldMapId = ctxView?.map?.worldMapId ?? ctxEdit?.map?.worldMapId;

    const query = useQuery<BookingQueryView, BookingQueryViewVariables>(BOOKING_QUERY_VIEW, {
        variables: {
            worldMapId,
            worldMapMarkerId: props.markerId
        },
        fetchPolicy: 'network-only'
    });

    const [bookingState, setBookingState] = useState<
        null | 'confirmed' | 'failed' | 'full' | 'available'
    >(null);
    const [confirmEmail, setConfirmEmail] = useState<string | null>(null);

    const bookingConfig = query?.data?.worldMarkerBookingConfiguration;
    const marker = query?.data?.worldMapMarker;

    function updateMarkerBookingAvailability(isBookingAvailable: boolean) {
        if (!ctxView || !ctxView.allMarkers) {
            return;
        }

        ctxView.setMarkers((curr) =>
            curr.map((marker) => {
                if (marker.worldMapMarkerId === props.markerId) {
                    return {
                        ...marker,
                        isBookingAvailable
                    };
                }
                return marker;
            })
        );
    }

    useEffect(() => {
        if (!marker) {
            return;
        }

        if (marker.isBookingAvailable) {
            setBookingState('available');
            updateMarkerBookingAvailability(true);
        } else {
            setBookingState('full');
            updateMarkerBookingAvailability(false);
        }
    }, [marker, query.loading, marker?.isBookingAvailable, worldMapId]);

    if (!bookingConfig || !marker || !worldMapId) {
        return null;
    }

    return (
        <S_DetailBooking className={props.className}>
            {bookingState === 'available' && (
                <DetailBookingForm
                    worldMapId={worldMapId}
                    worldMapMarkerId={props.markerId}
                    bookingConfig={bookingConfig}
                    onSubmit={(email) => {
                        setBookingState('confirmed');
                        setConfirmEmail(email);
                        updateMarkerBookingAvailability(false);
                    }}
                    onError={() => {
                        setBookingState('failed');
                        updateMarkerBookingAvailability(false);
                    }}
                />
            )}
            {bookingState === 'full' && <DetailBookingFull />}
            {bookingState === 'confirmed' && <DetailBookingConfirmed email={confirmEmail} />}
            {bookingState === 'failed' && <DetailBookingFailed worldMapId={worldMapId} />}
        </S_DetailBooking>
    );
};

const S_DetailBooking = styled.div`
    background-color: #f1f1f1;
    padding: 20px;
`;
